import { CallbackCardCodeEnum, CallbackPaymentMethod } from '@lib/types';
import { useGlobalState } from '../../../state/state';
import { ConfirmationCard } from '../ConfirmationCard';
import { numberHelper } from '../../../utils/numberHelper';
import { Box } from '@ace-clublabs/shared-component-library';
import { CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';
import { ConfirmationItemDetails } from '../ConfirmationCard';

export const PaymentDetails = () => {
  const [confirmation] = useGlobalState('confirmation');
  const { confirmationId, paymentDetails, amount, currentBillPlan, updatedBillPlan } = confirmation!;
  const { paymentType, last4, cardType, isScheduledPayment, autoPaySuccessful } = paymentDetails!;
  const billPlan = updatedBillPlan !== currentBillPlan ? updatedBillPlan : currentBillPlan;

  const mapPaymentTypeToPaymentTitle = {
    [CallbackPaymentMethod.Unitialized]: '',
    [CallbackPaymentMethod.Echeck]: 'E-check',
    [CallbackPaymentMethod.CreditCard]: 'Card',
    [CallbackPaymentMethod.DebitCard]: 'Card',
    [CallbackPaymentMethod.Google]: 'Google Pay',
    [CallbackPaymentMethod.Apple]: 'Apple Pay',
  };

  const paymentTypeTitle = mapPaymentTypeToPaymentTitle[paymentType as CallbackPaymentMethod];

  const mapIconName = {
    [CallbackCardCodeEnum.AMEX]: 'amex-logo',
    [CallbackCardCodeEnum.VISA]: 'visa-logo',
    [CallbackCardCodeEnum.MASTER]: 'mastercard-logo',
    [CallbackCardCodeEnum.DISC]: 'discover-logo',
    [CallbackCardCodeEnum.DINERS]: undefined,
    [CallbackCardCodeEnum.JCB]: undefined,
  };

  function getAmountSectionProps() {
    const amountSectionProps = {
      title: '',
      details: numberHelper.formatMoney(amount || 0), // does amount change based on the bill plan?
      className: '',
      isMonthlyBillPlan: false,
    };

    if (billPlan === CurrentPayPlan.RECURRINGMONTHLY) {
      amountSectionProps.title = 'New monthly amount due:';
      amountSectionProps.isMonthlyBillPlan = true;
      amountSectionProps.className = 'monthly-due';
    } else if (isScheduledPayment) {
      amountSectionProps.title = 'Amount scheduled:';
      amountSectionProps.className = 'amount-scheduled';
    } else {
      amountSectionProps.title = 'Amount paid today:';
      amountSectionProps.className = 'amount-paid';
    }

    return amountSectionProps;
  }

  const getPaymentDetailItems = () => {
    const paymentDetailsItems: ConfirmationItemDetails[] = [
      {
        title: 'Confirmation #',
        details: confirmationId as string,
        className: 'confirmation-number',
      },
      getAmountSectionProps(),
    ];

    if (paymentType && billPlan !== CurrentPayPlan.RECURRINGMONTHLY) {
      paymentDetailsItems.push({
        title: 'Payment method:',
        details: paymentTypeTitle,
        cardLastFour: last4,
        iconName: mapIconName[cardType as CallbackCardCodeEnum],
        className: 'payment-method',
      });
    }

    const enrolledInAutoPay = billPlan !== CurrentPayPlan.RECURRINGMONTHLY && paymentDetails?.isAutoPayRequested;

    if (enrolledInAutoPay && autoPaySuccessful) {
      paymentDetailsItems.push({
        autoPayEnrolled: true,
        className: 'autopay-enrolled',
      });
    }

    return paymentDetailsItems;
  };

  return (
    <Box className="payment-details-container">
      <ConfirmationCard cardTitle="Payment details" items={getPaymentDetailItems()} />
    </Box>
  );
};
